/*

Usage:

<AccordionMenu allowMultiple={true}>
  <AccordionMenu.Item id="item1">
    <AccordionMenu.ItemHeader>
      <h1>Click me to expand or collapse first item</h1>
    </AccordionMenu.ItemHeader>
    <AccordionMenu.ItemBody>
      <div>Show this when expanded</div>
    </AccordionMenu.ItemBody>
  <AccordionMenu.Item id="item2">
    <AccordionMenu.ItemHeader>
      <h1>Click me to expand or collapse second item</h1>
    </AccordionMenu.ItemHeader>
    <AccordionMenu.ItemBody>
      <div>Show this when expanded</div>
    </AccordionMenu.ItemBody>
</AccordionMenu>

 You can target the following selectors to customize to your particular situation if necessary:
  .accordion-component-menu
  .accordion-component-header
  .accordion-component-body
  .accordion-component-item
*/

import React, { createContext, useState, useContext } from 'react';
import styled from 'styled-components';

const StyledUl = styled.ul`
  .accordion-component-body.closed {
    height: 0;
    max-height: 0;
    opacity: 0;
    // I decided to avoid an approach using 'display: none' to allow for smoother transitions if we want to add them later
    // For some reason the hidden content had a higher z-index and was clickable. It's much simpler to disable pointer-events
    // than solve the z-index issue
    pointer-events: none;
  }

  .accordion-component-body.open {
    opacity: 1;
  }
`;

const AccordionContext = createContext();

function AccordionMenu({ children, allowMultiple = false }) {
  const [openItems, setOpenItems] = useState(new Set());

  const toggleItem = (itemId) => {
    setOpenItems((previousValue) => {
      const newSet = new Set(previousValue);

      if (allowMultiple) {
        if (newSet.has(itemId)) {
          newSet.delete(itemId);
        } else {
          newSet.add(itemId);
        }
      } else {
        // Exclusive mode - only one item open at a time
        if (newSet.has(itemId)) {
          newSet.clear(); // Close the currently open item
        } else {
          newSet.clear(); // Close all items first
          newSet.add(itemId); // Then open the clicked item
        }
      }

      return newSet;
    });
  };

  const contextValue = {
    openItems,
    toggleItem,
  };

  return (
    <AccordionContext.Provider value={contextValue}>
      <StyledUl className="accordion-component-menu">{children}</StyledUl>
    </AccordionContext.Provider>
  );
}

function AccordionItem({ children, id }) {
  // Generate a random ID if none is provided
  // Use useState to ensure the ID remains constant across re-renders
  const [generatedId] = useState(() => id || crypto.randomUUID());

  return (
    <AccordionContext.Provider
      value={{
        ...useContext(AccordionContext),
        itemId: generatedId,
      }}
    >
      <li className="accordion-component-item">{children}</li>
    </AccordionContext.Provider>
  );
}

function AccordionItemHeader({ children }) {
  const { toggleItem, itemId, openItems } = useContext(AccordionContext);
  const isOpen = openItems.has(itemId);

  const handleClick = (e) => {
    toggleItem(itemId);
  };

  return (
    <div
      className={`accordion-component-header ${isOpen ? 'open' : ''}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      aria-expanded={isOpen}
      aria-controls={`accordion-component-body-${itemId}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleItem(itemId);
        }
      }}
    >
      {children}
    </div>
  );
}

// Accordion Item Body (collapsible content)
function AccordionItemBody({ children }) {
  const { itemId, openItems } = useContext(AccordionContext);
  const isOpen = openItems.has(itemId);

  return (
    <div
      id={`accordion-component-body-${itemId}`}
      className={`accordion-component-body ${isOpen ? 'open' : 'closed'}`}
      aria-hidden={!isOpen}
      aria-labelledby={`accordion-component-header-${itemId}`}
    >
      {children}
    </div>
  );
}

AccordionMenu.Item = AccordionItem;
AccordionMenu.ItemHeader = AccordionItemHeader;
AccordionMenu.ItemBody = AccordionItemBody;

export default AccordionMenu;
